/**
 * Courseware MobX Store
 * Manages state for courseware list, current course, player, and progress tracking
 */

import { action, computed, makeObservable, observable, runInAction, reaction } from "mobx";
import remotedev from "mobx-remotedev";
import {
  CoursewareItemDI,
  CoursewareDetailDI,
  UpdateProgressRequestDI,
} from "./types";
import {
  getCoursewareConfig,
  getCoursewareList,
  getCoursewareDetail,
  updateCoursewareProgress,
} from "./services/coursewareService";
import appStore from "../../base/stores/AppStore";

export class CoursewareStore {
  // Configuration
  requireAuthentication: boolean = false;
  hasAccess: boolean = false;
  configLoaded: boolean = false;

  // Course list
  courseList: CoursewareItemDI[] = [];

  // Current course being viewed
  currentCourse: CoursewareDetailDI | null = null;

  // Player state
  isPlayerOpen: boolean = false;

  // Loading states
  isLoadingConfig: boolean = false;
  isLoadingList: boolean = false;
  isLoadingDetail: boolean = false;
  isSavingProgress: boolean = false;

  // Error states
  configError: string | null = null;
  listError: string | null = null;
  detailError: string | null = null;
  progressError: string | null = null;

  constructor() {
    makeObservable(this, {
      // Observables
      requireAuthentication: observable,
      hasAccess: observable,
      configLoaded: observable,
      courseList: observable,
      assignedCourses: computed,
      currentCourseIndex: computed,
      currentCourse: observable,
      isPlayerOpen: observable,
      isLoadingConfig: observable,
      isLoadingList: observable,
      isLoadingDetail: observable,
      isSavingProgress: observable,
      configError: observable,
      listError: observable,
      detailError: observable,
      progressError: observable,

      // Actions
      fetchConfig: action,
      fetchCourseList: action,
      loadCourse: action,
      openPlayer: action,
      closePlayer: action,
      saveProgress: action,
      clearErrors: action,
      resetCurrentCourse: action,
      navigateToNext: action,
      navigateToPrev: action,
    });

    // Fetch config immediately — the config endpoint doesn't require authentication
    // and StandaloneModeProvider blocks the entire app until configLoaded is true.
    // Waiting for appStore.initDataLoaded would cause a deadlock since initData
    // is loaded by page components that are children of StandaloneModeProvider.
    this.fetchConfig();

    // Re-fetch config when initData loads to get accurate hasAccess with auth session
    reaction(
      () => appStore.initDataLoaded,
      (isLoaded) => {
        if (isLoaded) {
          // Re-fetch in the background without resetting configLoaded,
          // so the app stays visible during the refresh (avoids full-screen loader flash).
          this.fetchConfig(true);
        }
      },
      { name: 'CoursewareStore-initData-watcher' }
    );
  }

  /**
   * Fetch global courseware configuration
   * Determines if authentication is required for courseware access
   */
  fetchConfig = async (force = false) => {
    if (!force && this.configLoaded) {
      // console.log('[CoursewareStore] fetchConfig skipped (already loaded, force=false)');
      return;
    }
    
    if (this.isLoadingConfig) {
      // console.log('[CoursewareStore] fetchConfig skipped (already loading)');
      return;
    }
    // console.log('[CoursewareStore] fetchConfig starting (force=' + force + ')');
    this.isLoadingConfig = true;
    this.configError = null;

    try {
      const config = await getCoursewareConfig();

      runInAction(() => {
        this.requireAuthentication = config.requireAuthentication;
        this.hasAccess = config.hasAccess;
        this.configLoaded = true;
        this.isLoadingConfig = false;
        // console.log('[CoursewareStore] fetchConfig done: requireAuth=' + config.requireAuthentication + ', hasAccess=' + config.hasAccess);
      });
    } catch (error: any) {
      runInAction(() => {
        this.configError = error?.message || "Failed to load configuration";
        this.isLoadingConfig = false;
      });
      console.error("[CoursewareStore] Error fetching courseware config:", error);
    }
  };

  /**
   * Fetch list of all available courseware items
   * Includes user-specific completion status if authenticated
   */
  fetchCourseList = async () => {
    this.isLoadingList = true;
    this.listError = null;

    try {
      const list = await getCoursewareList();

      runInAction(() => {
        this.courseList = list;
        this.isLoadingList = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.listError = error?.message || "Failed to load courseware list";
        this.isLoadingList = false;
      });
      console.error("Error fetching courseware list:", error);
    }
  };

  /**
   * Load detailed information for a specific courseware item
   * Prepares data for the SCORM player
   * 
   * @param courseId - Courseware item ID (Umbraco node ID)
   */
  loadCourse = async (courseId: number) => {
    this.isLoadingDetail = true;
    this.detailError = null;

    try {
      const course = await getCoursewareDetail(courseId);

      runInAction(() => {
        this.currentCourse = course;
        this.isLoadingDetail = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.detailError = error?.message || "Failed to load courseware details";
        this.isLoadingDetail = false;
      });
      console.error("Error loading courseware detail:", error);
    }
  };

  /**
   * Open the SCORM player modal
   * Call after loadCourse() has completed
   */
  openPlayer = () => {
    // console.log('[SCORM DEBUG] Store: openPlayer called for course', this.currentCourse?.Id);
    if (!this.currentCourse) {
      console.error("Cannot open player: no course loaded");
      return;
    }
    this.isPlayerOpen = true;
  };

  /**
   * Close the SCORM player modal
   * Does NOT clear currentCourse to allow subsequent reopening
   */
  closePlayer = () => {
    this.isPlayerOpen = false;

    // Remove ?courseId= from URL when closing the modal
    const url = new URL(window.location.href);
    if (url.searchParams.has("courseId")) {
      url.searchParams.delete("courseId");
      window.history.replaceState({}, "", url.pathname + (url.search || ""));
    }
  };

  /**
   * Save user progress to backend
   * Called by SCORM API on LMSCommit() or LMSFinish()
   * 
   * @param data - SCORM runtime data to save
   */
  saveProgress = async (data: UpdateProgressRequestDI) => {
    if (!this.currentCourse) {
      console.error("Cannot save progress: no course loaded");
      return;
    }

    this.isSavingProgress = true;
    this.progressError = null;

    try {
      const response = await updateCoursewareProgress(this.currentCourse.Id, data);

      runInAction(() => {
        this.isSavingProgress = false;

        // Update local state from backend response
        if (this.currentCourse && response.Success) {
          this.currentCourse.ProgressPercent = response.ProgressPercent;
          this.currentCourse.Status = response.Status;
          
          // Also update from the request data we sent
          if (data.LessonStatus) {
            this.currentCourse.LessonStatus = data.LessonStatus;
          }
          if (data.ScoreRaw !== undefined) {
            this.currentCourse.ScoreRaw = data.ScoreRaw;
          }
          if (data.SuspendData !== undefined) {
            this.currentCourse.SuspendData = data.SuspendData;
          }
          if (data.LessonLocation !== undefined) {
            this.currentCourse.LessonLocation = data.LessonLocation;
          }
          

        } else if (!response.Success) {
          this.progressError = response.ErrorMessage || "Failed to save progress";
        }
      });
    } catch (error: any) {
      runInAction(() => {
        this.progressError = error?.message || "Failed to save progress";
        this.isSavingProgress = false;
      });
      console.error("Error saving courseware progress:", error);
    }
  };

  /**
   * Clear all error messages
   */
  clearErrors = () => {
    this.configError = null;
    this.listError = null;
    this.detailError = null;
    this.progressError = null;
  };

  /**
   * Get courses assigned to the current user
   */
  get assignedCourses(): CoursewareItemDI[] {
    return this.courseList.filter(course => !!course.AssignmentDate);
  }

  /**
   * Get the index of the current course in the courseList
   */
  get currentCourseIndex(): number {
    if (!this.currentCourse) return -1;
    return this.assignedCourses.findIndex(c => c.Id === this.currentCourse!.Id);
  }

  /**
   * Navigate to the next course in the list
   */
  navigateToNext = async () => {
    const idx = this.currentCourseIndex;
    if (idx < 0 || idx >= this.assignedCourses.length - 1) return;
    const nextCourse = this.assignedCourses[idx + 1];
    await this.loadCourse(nextCourse.Id);
  };

  /**
   * Navigate to the previous course in the list
   */
  navigateToPrev = async () => {
    const idx = this.currentCourseIndex;
    if (idx <= 0) return;
    const prevCourse = this.assignedCourses[idx - 1];
    await this.loadCourse(prevCourse.Id);
  };

  /**
   * Reset current course and close player
   * Called when navigating away from courseware
   */
  resetCurrentCourse = () => {
    this.currentCourse = null;
    this.isPlayerOpen = false;
    this.detailError = null;
  };
}

const coursewareStore = new CoursewareStore();
export default remotedev(coursewareStore, { name: "CoursewareStore" });
